import { err, extractErrorDetails, ok } from '@src/services/common/Result';
import { populateVideoVersionWithResources } from '@src/services/VideoResourcesProvider';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';

import type { Result } from '@src/services/common/Result';
import type { VideoResourceField } from '@src/services/VideoResourcesProvider';
import type { VideoVersion } from '@src/types.ts/story';

/** Every resource a version can reference in S3. */
const RESOURCE_FIELDS: VideoResourceField[] = [
  'videoSourceSnapshot',
  'transcriptionSnapshot',
  'karaokeData',
  'scenes',
];

/**
 * Same page size as Creator Studio's History tab (PLAT-12038). Each version is
 * fetched with its full video payload, which is what keeps this from going higher.
 */
export const VIDEO_VERSIONS_PAGE_SIZE = 10;

/** The one repository read this service needs (`VideoRepository` satisfies it). */
export interface VideoVersionSource {
  getVersionsByVideoId(
    id: string,
    pageLimit: number,
    page?: number,
    sizeAware?: boolean,
  ): Promise<{ videoVersions: VideoVersion[]; totalCount: number }>;
}

export interface VideoVersionHistoryServiceDeps {
  videoRepository: VideoVersionSource;
  /** Injectable for tests; defaults to the S3 resource loader. */
  populateResources?: (
    storyId: string,
    version: VideoVersion,
    fields: VideoResourceField[],
  ) => Promise<VideoVersion>;
}

export interface VideoVersionPage {
  versions: VideoVersion[];
  page: number;
  totalPages: number;
  /** The version Dato marks as the record's current state, when on this page. */
  headVersionId?: string;
}

/**
 * Reads a video record's DatoCMS version history and loads one version's
 * resources (videoSource, transcript, karaoke, scenes) so an editor can apply
 * it. Shared by any surface that shows video history; holds no state.
 */
export class VideoVersionHistoryService {
  private readonly _videoRepository: VideoVersionSource;
  private readonly _populateResources: NonNullable<
    VideoVersionHistoryServiceDeps['populateResources']
  >;
  private readonly _logger = LogManager.getLogger(
    'service:VideoVersionHistoryService',
  );

  constructor(deps: VideoVersionHistoryServiceDeps) {
    this._videoRepository = deps.videoRepository;
    this._populateResources =
      deps.populateResources ??
      ((storyId, version, fields) =>
        populateVideoVersionWithResources(storyId, version, fields));
  }

  async listVersions(input: {
    videoId: string;
    page: number;
    isExternalUser: boolean;
  }): Promise<Result<VideoVersionPage>> {
    try {
      const { videoVersions, totalCount } =
        await this._videoRepository.getVersionsByVideoId(
          input.videoId,
          VIDEO_VERSIONS_PAGE_SIZE,
          input.page,
          input.isExternalUser,
        );
      return ok({
        versions: videoVersions,
        page: input.page,
        totalPages: Math.ceil(totalCount / VIDEO_VERSIONS_PAGE_SIZE),
        headVersionId: videoVersions.find(v => v.meta.is_current)?.versionId,
      });
    } catch (e) {
      this._logger.error('Failed to load version history', {
        videoId: input.videoId,
        page: input.page,
        ...extractErrorInfo(e),
      });
      return err(
        'EXTERNAL',
        'Could not load version history',
        extractErrorDetails(e),
      );
    }
  }

  /** Fetch the resources a version references so it can be loaded into an editor. */
  async loadVersion(input: {
    storyId: string;
    version: VideoVersion;
  }): Promise<Result<VideoVersion>> {
    const { storyId, version } = input;
    const metadata = version.resourcesMetadata;
    const fields = metadata
      ? RESOURCE_FIELDS.filter(field => field in metadata)
      : [];
    try {
      const populated = await this._populateResources(storyId, version, fields);
      if (!Array.isArray(populated.videoSource?.elements)) {
        return err('NOT_FOUND', 'This version has no saved timeline');
      }
      return ok(populated);
    } catch (e) {
      this._logger.error('Failed to load video version', {
        versionId: version.versionId,
        ...extractErrorInfo(e),
      });
      return err(
        'EXTERNAL',
        'Could not open this version',
        extractErrorDetails(e),
      );
    }
  }
}
